import posthog from "posthog-js";

/**
 * Telemetry for the inbound softphone's registration lifecycle.
 *
 * The provider had no telemetry at all, which made a real outage
 * undiagnosable: a clinic reported staff not receiving calls and there was no
 * way, from outside their browser, to answer "is this person's softphone
 * actually registered right now?". Server logs show a subscriber token being
 * issued; nothing shows whether `client.register()` then succeeded. PostHog
 * session recordings do not capture console output for this tenant, so the
 * provider's existing `console.error` calls never leave the machine.
 *
 * These events close that gap. `softphone_registered` is the important one —
 * its absence for a user who is signed in and in a ring group is the signal
 * that their line is silently offline.
 *
 * NO PHI. Caller names and numbers never appear here: the busy-reject path in
 * particular has a caller in hand and must not pass it through. Properties are
 * limited to the subscriber's own identity, error names, counters and
 * durations.
 */

/** How long to suppress a repeat of the same failure with the same reason.
 *
 * The provider's retry ladder backs off to a 60s cap, but a wake event
 * collapses that backoff and wake events are only rate-limited to 5s. A tab on
 * a flapping network can therefore attempt — and fail — every 5 seconds
 * indefinitely. Unthrottled that is ~720 events/hour/user of one repeating
 * fact. The first occurrence is what carries the information; the rest are
 * volume.
 */
const FAILURE_THROTTLE_MS = 60_000;

const lastFailureAt = new Map<string, number>();

export type SoftphoneFailureEvent =
  | "softphone_register_failed"
  | "softphone_register_timeout"
  | "softphone_token_missing"
  | "softphone_token_refresh_failed"
  | "softphone_mic_denied"
  | "softphone_mic_unavailable";

export type SoftphoneEvent =
  | "softphone_registered"
  | "softphone_recovery_attempt"
  | "softphone_incoming_rejected_busy"
  // One per Answer click. Not throttled: each is a distinct call, and the
  // pair gives the answer success rate and latency per user and connection.
  | "softphone_answered"
  | "softphone_answer_failed";

type Properties = Record<string, string | number | boolean | null | undefined>;

/** Capture an event that is naturally bounded (one per init, call or wake). */
export function captureSoftphoneEvent(
  event: SoftphoneEvent,
  properties: Properties = {},
): void {
  posthog.capture(event, properties);
}

/**
 * Capture a failure, at most once per minute per (event, reason).
 *
 * Throttled rather than deduped-forever: a persistent failure should keep
 * showing up so it can be seen as ongoing rather than as a single blip an hour
 * ago, but at a rate that describes the problem instead of flooding it.
 */
export function captureSoftphoneFailure(
  event: SoftphoneFailureEvent,
  properties: Properties = {},
): void {
  const key = `${event}:${properties.reason ?? ""}`;
  const now = Date.now();
  const previous = lastFailureAt.get(key);
  if (previous !== undefined && now - previous < FAILURE_THROTTLE_MS) return;
  lastFailureAt.set(key, now);
  posthog.capture(event, properties);
}

/** Test seam: the throttle is module state and would leak between tests. */
export function resetSoftphoneTelemetryThrottle(): void {
  lastFailureAt.clear();
}
